import React, { useEffect, useState } from "react";
import { MessageSquareWarning } from "lucide-react";
import { Modal } from "./Modal";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";

const MARKETS = ["BR", "US", "PT", "ES", "FR", "IT"];
const LANGS = ["pt", "pt-PT", "en", "es", "fr", "it"];
const STATUSES = ["new", "in_review", "resolved", "ignored"];
const CATEGORIES = ["bug", "payment", "access", "visual", "suggestion", "other"];
const DOT = { new: "bg-red-400", in_review: "bg-yellow-400", resolved: "bg-emerald-400", ignored: "bg-slate-400" };
const EMPTY = { status: "", market: "", lang: "", category: "", user: "", q: "", date_from: "", date_to: "" };
const field = "rounded-xl border border-xmas-gold/20 bg-xmas-navy/60 px-3 py-2 text-xs text-xmas-snow focus:border-xmas-gold focus:outline-none";
const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

function StatusBadge({ s, t }) {
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2 py-0.5 text-xs text-xmas-snow" data-testid={`fb-status-${s}`}><span className={`h-2 w-2 rounded-full ${DOT[s] || DOT.ignored}`} />{t(`fb.st.${s}`)}</span>;
}

function Detail({ item, t, onStatus, onClose }) {
  if (!item) return null;
  const row = (k, v, tid) => <div className="flex gap-3 border-b border-white/5 py-1.5 text-sm"><span className="w-32 shrink-0 text-xs uppercase tracking-wider text-xmas-slate">{k}</span><span className="break-words text-xmas-snow" data-testid={tid}>{v ?? "—"}</span></div>;
  return (
    <Modal open onClose={onClose} title={t("fb.admin.detail")} testid="fb-detail-modal">
      <div data-testid="fb-detail">
        {row("ID", item.report_id, "fb-detail-id")}
        {row(t("fb.admin.date"), fmt(item.created_at))}
        {row(t("fb.admin.user"), item.user_name || (item.user_id ? item.user_id : t("fb.admin.visitor")), "fb-detail-user")}
        {row("E-mail", item.user_email, "fb-detail-email")}
        {row(t("fb.admin.country"), item.market ? `${item.market}${item.ip_country && item.ip_country !== item.market ? ` (IP ${item.ip_country})` : ""}` : item.ip_country, "fb-detail-market")}
        {row(t("fb.admin.lang"), item.lang, "fb-detail-lang")}
        {row(t("fb.page"), item.page, "fb-detail-page")}
        {row(t("fb.category"), t(`fb.cat.${item.category}`), "fb-detail-category")}
        {row(t("fb.admin.origin"), item.origin, "fb-detail-origin")}
        <p className="mt-4 text-xs uppercase tracking-wider text-xmas-slate">{t("fb.desc")}</p>
        <p className="mt-1 whitespace-pre-wrap rounded-xl border border-xmas-gold/15 bg-xmas-navy/50 p-3 text-sm text-xmas-snow" data-testid="fb-detail-description">{item.description}</p>
        {item.tech && Object.keys(item.tech).length > 0 && (
          <details className="mt-3 text-xs text-xmas-slate"><summary className="cursor-pointer uppercase tracking-wider">{t("fb.admin.tech")}</summary>
            <pre className="mt-2 whitespace-pre-wrap break-all rounded-xl bg-xmas-navy/50 p-3 text-[11px]" data-testid="fb-detail-tech">{Object.entries(item.tech).map(([k, v]) => `${k}: ${v}`).join("\n")}</pre></details>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-xmas-slate">{t("fb.admin.status")}</span>
          <select value={item.status} onChange={(e) => onStatus(item.report_id, e.target.value)} className={field} data-testid="fb-detail-status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{t(`fb.st.${s}`)}</option>)}
          </select>
          <StatusBadge s={item.status} t={t} />
        </div>
        {item.history?.length > 0 && (
          <div className="mt-4 text-xs text-xmas-slate"><p className="uppercase tracking-wider">{t("fb.admin.history")}</p>
            {item.history.slice().reverse().map((h, i) => <p key={i} className="mt-1">{fmt(h.at)} · {t(`fb.st.${h.status}`)} · {h.by}</p>)}</div>
        )}
      </div>
    </Modal>
  );
}

export default function FeedbackAdmin() {
  const { t } = useI18n();
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const load = React.useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/feedback", { params }).then(({ data }) => setData(data)).catch(() => setData({ items: [], counts: {} }));
  }, [filters]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const changeStatus = async (id, status) => {
    const { data: upd } = await api.patch(`/admin/feedback/${id}`, { status });
    setSel(upd); load();
  };

  const c = data?.counts || {};
  const stats = [["total", t("fb.admin.total"), c.total], ["new", t("fb.st.new"), c.new], ["in_review", t("fb.st.in_review"), c.in_review], ["resolved", t("fb.st.resolved"), c.resolved]];
  return (
    <section className="mt-12" data-testid="feedback-admin">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-xl text-xmas-snow"><MessageSquareWarning className="h-5 w-5 text-xmas-gold" /> {t("fb.admin.title")}</h2>
        {data && <span className={`text-xs ${data.support_email_configured ? "text-emerald-400" : "text-red-400"}`} data-testid="fb-email-status">● {data.support_email_configured ? t("fb.admin.email_on") : t("fb.admin.email_off")}</span>}
      </div>
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map(([k, label, v]) => (
          <div key={k} className={`glass rounded-2xl p-4 ${k === "new" && v > 0 ? "border-red-400/50 shadow-[0_0_18px_rgba(248,113,113,.25)]" : ""}`} data-testid={`fb-count-${k}`}>
            <p className={`font-mono-num text-2xl font-bold ${k === "new" && v > 0 ? "text-red-300" : "text-xmas-snow"}`}>{v ?? 0}</p>
            <p className="text-xs uppercase tracking-wider text-xmas-slate">{label}</p>
          </div>
        ))}
      </div>
      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-8" data-testid="fb-filters">
        <select value={filters.status} onChange={set("status")} className={field} data-testid="fb-filter-status"><option value="">{`${t("fb.admin.status")}: ${t("fb.admin.all")}`}</option>{STATUSES.map((s) => <option key={s} value={s}>{t(`fb.st.${s}`)}</option>)}</select>
        <select value={filters.market} onChange={set("market")} className={field} data-testid="fb-filter-market"><option value="">{`${t("fb.admin.country")}: ${t("fb.admin.all")}`}</option>{MARKETS.map((m) => <option key={m} value={m}>{m}</option>)}</select>
        <select value={filters.lang} onChange={set("lang")} className={field} data-testid="fb-filter-lang"><option value="">{`${t("fb.admin.lang")}: ${t("fb.admin.all")}`}</option>{LANGS.map((l) => <option key={l} value={l}>{l}</option>)}</select>
        <select value={filters.category} onChange={set("category")} className={field} data-testid="fb-filter-category"><option value="">{`${t("fb.category")}: ${t("fb.admin.all")}`}</option>{CATEGORIES.map((x) => <option key={x} value={x}>{t(`fb.cat.${x}`)}</option>)}</select>
        <input type="date" value={filters.date_from} onChange={set("date_from")} className={field} title={t("fb.admin.from")} data-testid="fb-filter-from" />
        <input type="date" value={filters.date_to} onChange={set("date_to")} className={field} title={t("fb.admin.to")} data-testid="fb-filter-to" />
        <input value={filters.user} onChange={set("user")} placeholder={t("fb.admin.user")} className={field} data-testid="fb-filter-user" />
        <input value={filters.q} onChange={set("q")} placeholder={t("fb.admin.search")} className={field} data-testid="fb-filter-q" />
      </div>
      {Object.values(filters).some(Boolean) && <button onClick={() => setFilters(EMPTY)} className="mb-3 text-xs text-xmas-gold underline-offset-4 hover:underline" data-testid="fb-filter-clear">{t("fb.admin.clear")}</button>}
      <div className="glass overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="border-b border-xmas-gold/15 text-xs uppercase tracking-wider text-xmas-slate">
            <tr>{[t("fb.admin.date"), t("fb.admin.country"), t("fb.admin.lang"), t("fb.admin.user"), t("fb.category"), t("fb.page"), t("fb.admin.summary"), t("fb.admin.status")].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="fb-table-body">
            {(data?.items || []).map((r) => (
              <tr key={r.report_id} onClick={() => setSel(r)} className="cursor-pointer border-b border-white/5 transition hover:bg-white/5" data-testid={`fb-row-${r.report_id}`}>
                <td className="whitespace-nowrap px-3 py-2.5 text-xs text-xmas-slate">{fmt(r.created_at)}</td>
                <td className="px-3 py-2.5 text-xmas-snow">{r.market || r.ip_country || "—"}</td>
                <td className="px-3 py-2.5 text-xmas-slate">{r.lang}</td>
                <td className="max-w-[160px] truncate px-3 py-2.5 text-xmas-snow">{r.user_email || t("fb.admin.visitor")}</td>
                <td className="px-3 py-2.5 text-xmas-slate">{t(`fb.cat.${r.category}`)}</td>
                <td className="max-w-[140px] truncate px-3 py-2.5 text-xmas-slate">{r.page || "—"}</td>
                <td className="max-w-[260px] truncate px-3 py-2.5 text-xmas-snow">{r.description}</td>
                <td className="px-3 py-2.5"><StatusBadge s={r.status} t={t} /></td>
              </tr>
            ))}
            {data && data.items?.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-xmas-slate" data-testid="fb-empty">{t("fb.admin.empty")}</td></tr>}
          </tbody>
        </table>
      </div>
      <Detail item={sel} t={t} onStatus={changeStatus} onClose={() => setSel(null)} />
    </section>
  );
}
